import React from 'react';
import { Platform, ActivityIndicator, Dimensions } from 'react-native';
import { OverlayViewF, OVERLAY_MOUSE_TARGET } from '@react-google-maps/api';
import Icon from 'react-native-vector-icons/MaterialIcons';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const isMobile = SCREEN_WIDTH < 768;
const MOBILE_NAV_BAR_HEIGHT = 85;
const MOBILE_POPUP_VERTICAL_SPACING = 48;
const MOBILE_MAX_POPUP_HEIGHT_AVAILABLE = SCREEN_HEIGHT - MOBILE_NAV_BAR_HEIGHT - MOBILE_POPUP_VERTICAL_SPACING;
const POPUP_MAX_WIDTH = isMobile ? Math.min(SCREEN_WIDTH * 0.92, 360) : Math.min(360, SCREEN_WIDTH * 0.9);
const POPUP_MAX_HEIGHT = isMobile ? Math.max(280, MOBILE_MAX_POPUP_HEIGHT_AVAILABLE) : Math.min(480, SCREEN_HEIGHT * 0.65);

const TournamentPopup = ({ tournament, visible, onClose, onViewTournament, onViewPlayerMode }) => {
  if (!visible || !tournament) return null;

  const participants = tournament.participants?.items || [];
  const participantCount = participants.length;
  
  const isActive = tournament.status === 'IN_PROGRESS';
  const isRegistrationOpen = tournament.status === 'REGISTRATION';
  const statusColor = isActive ? '#4CAF50' : isRegistrationOpen ? '#FF9800' : '#9E9E9E';
  const statusText = isActive ? 'In Progress' : isRegistrationOpen ? 'Registration Open' : tournament.status?.replace('_', ' ');

  const getPixelPositionOffset = (width, height) => ({
    x: -width / 2,
    y: isMobile ? 24 : -450,
  });

  // Get location from the club associated with the tournament
  const clubLocation = tournament?.club ? {
    lat: tournament.club.latitude || 49.2827,
    lng: tournament.club.longitude || -123.1207,
  } : { lat: 49.2827, lng: -123.1207 };

  const popupContent = (
    <div
      className="map-popup-content"
      style={{
        width: `${POPUP_MAX_WIDTH}px`,
        maxWidth: '92vw',
        maxHeight: `${POPUP_MAX_HEIGHT}px`,
        backgroundColor: 'white',
        borderRadius: '20px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        animation: 'slideUpFade 0.3s ease-out',
        zIndex: 100000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Close Button - high z-index so it stays clickable above content */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          width: '36px',
          height: '36px',
          borderRadius: '18px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 100002,
        }}
      >
        <Icon name="close" size={20} color="white" />
      </div>

      {/* Header with gradient */}
      <div
        style={{
          background: `linear-gradient(135deg, ${statusColor} 0%, ${statusColor}dd 100%)`,
          padding: isMobile ? '14px 16px' : '24px 20px',
          color: 'white',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, marginBottom: '8px' }}>
          <div
            style={{
              width: isMobile ? 40 : 48,
              height: isMobile ? 40 : 48,
              borderRadius: isMobile ? 20 : 24,
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? 20 : 24,
            }}
          >
            🏆
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 16 : 20, fontWeight: 'bold' }}>{tournament.name}</div>
            <div style={{ fontSize: isMobile ? 11 : 13, opacity: 0.9, marginTop: '2px' }}>
              {tournament.type?.replace('_', ' ')} • {tournament.totalRounds || '?'} Rounds
            </div>
          </div>
        </div>
        
        {/* Status badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          <div
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '4px',
              backgroundColor: isActive ? '#fff' : 'rgba(255,255,255,0.7)',
              animation: isActive ? 'pulse 1.5s infinite' : 'none',
            }}
          />
          {statusText}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch' }}>
        {/* Tournament Info */}
        <div style={{ padding: isMobile ? '12px 16px' : '16px 20px' }}>
          {/* Stats Row */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              padding: isMobile ? '12px' : '16px',
              marginBottom: isMobile ? 12 : 16,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 'bold', color: '#333' }}>{participantCount}</div>
              <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>Players</div>
            </div>
            <div style={{ width: '1px', backgroundColor: '#ddd' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 'bold', color: '#333' }}>
                {tournament.currentRound || 0}/{tournament.totalRounds || '?'}
              </div>
              <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>Round</div>
            </div>
            <div style={{ width: '1px', backgroundColor: '#ddd' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>
                {tournament.isPaid ? `$${tournament.entryFee || 0}` : 'Free'}
              </div>
              <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>Entry</div>
            </div>
          </div>

          {/* Club info */}
          {tournament.club && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '10px',
                marginBottom: '16px',
              }}
            >
              <Icon name="groups" size={20} color="#76AF87" />
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                  {tournament.club.clubName || 'Chess Club'}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Organizer</div>
              </div>
            </div>
          )}

          {/* Description */}
          {tournament.description && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#888', marginBottom: '6px', textTransform: 'uppercase' }}>
                About
              </div>
              <div style={{ fontSize: '14px', color: '#555', lineHeight: '1.5' }}>
                {tournament.description}
              </div>
            </div>
          )}

          {/* Participants Preview */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#888', marginBottom: '10px', textTransform: 'uppercase' }}>
              Participants ({participantCount})
            </div>
            {participantCount === 0 ? (
              <div style={{ textAlign: 'center', color: '#999', fontSize: '13px', padding: '12px' }}>
                No participants yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {participants.slice(0, 8).map((participant) => (
                  <div
                    key={participant.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '20px',
                      fontSize: '12px',
                    }}
                  >
                    <div
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '12px',
                        backgroundColor: '#4CAF50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: 'bold',
                      }}
                    >
                      {(participant.guestName || 'P')[0].toUpperCase()}
                    </div>
                    <span style={{ fontWeight: '500', color: '#333' }}>
                      {participant.guestName || `Player ${participant.seedNumber || ''}`}
                    </span>
                  </div>
                ))}
                {participantCount > 8 && (
                  <div
                    style={{
                      padding: '6px 10px',
                      fontSize: '12px',
                      color: '#666',
                    }}
                  >
                    +{participantCount - 8} more
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ padding: isMobile ? '0 16px 16px' : '0 20px 20px', display: 'flex', gap: '10px' }}>
          <button
            onClick={onViewTournament}
            style={{
              flex: 1,
              padding: isMobile ? '12px' : '14px',
              backgroundColor: statusColor,
              border: 'none',
              borderRadius: '12px',
              fontSize: isMobile ? 13 : 14,
              fontWeight: '600',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = `0 4px 12px ${statusColor}50`;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <Icon name="visibility" size={18} color="white" />
            View Tournament
          </button>
          
          <button
            onClick={onViewPlayerMode}
            style={{
              flex: 1,
              padding: isMobile ? '12px' : '14px',
              backgroundColor: '#f5f5f5',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#333',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#e0e0e0'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f5f5f5'}
          >
            <Icon name="person" size={18} color="#333" />
            Player View
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes slideUpFade {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </div>
  );

  if (Platform.OS === 'web') {
    return (
      <OverlayViewF
        position={clubLocation}
        mapPaneName={OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={getPixelPositionOffset}
      >
        {popupContent}
      </OverlayViewF>
    );
  }

  // For mobile (not implemented yet)
  return null;
};

export default TournamentPopup;

